import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  ReactNode,
} from "react";
import { clientService } from "@/services";
import { ApiError } from "@/services/ApiService";
import type { ClientDto } from "@shared/types/ClientDto";
import type { ServiceOrderDto } from "@shared/types/ServiceOrderDto";

const ACCESS_DENIED_CODES = ["CLIENT_NOT_FOUND", "CLIENT_ARCHIVED"];

interface ClientDataContextType {
  clientInfo: ClientDto | null;
  projects: ServiceOrderDto[];
  isFetchingProjects: boolean;
  isFetchingClientInfo: boolean;
  error: string | null;
  accessDenied: boolean;
  accessDeniedReason: string | null;
  getInfo: () => Promise<void>;
  fetchProjects: () => Promise<void>;
}

const ClientDataContext = createContext<ClientDataContextType | null>(null);

export function ClientDataProvider({ children }: { children: ReactNode }) {
  const [clientInfo, setClientInfo] = useState<ClientDto | null>(null);
  const [projects, setProjects] = useState<ServiceOrderDto[]>([]);
  const [isFetchingClientInfo, setIsFetchingClientInfo] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isFetchingProjects, setIsFetchingProjects] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const [accessDeniedReason, setAccessDeniedReason] = useState<string | null>(
    null,
  );

  const getInfo = useCallback(async () => {
    setIsFetchingClientInfo(true);
    setError(null);
    try {
      const data = await clientService.getClientInfo();
      setClientInfo(data);
      setAccessDenied(false);
      setAccessDeniedReason(null);
    } catch (err) {
      if (
        err instanceof ApiError &&
        err.errorCode &&
        ACCESS_DENIED_CODES.includes(err.errorCode)
      ) {
        setAccessDenied(true);
        setAccessDeniedReason(
          err.errorCode === "CLIENT_ARCHIVED"
            ? "Your client account has been archived."
            : "Your account is not associated with an active client.",
        );
      } else {
        setError(
          err instanceof Error ? err.message : "Failed to fetch client info",
        );
      }
    } finally {
      setIsFetchingClientInfo(false);
    }
  }, []);

  const fetchProjects = useCallback(async () => {
    setIsFetchingProjects(true);
    setError(null);
    try {
      const data = await clientService.getClientProjects();
      setProjects(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch projects");
    } finally {
      setIsFetchingProjects(false);
    }
  }, []);

  useEffect(() => {
    getInfo();
  }, [getInfo]);

  useEffect(() => {
    if (!accessDenied && clientInfo) {
      fetchProjects();
    }
  }, [accessDenied, clientInfo, fetchProjects]);

  return (
    <ClientDataContext.Provider
      value={{
        clientInfo,
        projects,
        isFetchingProjects,
        isFetchingClientInfo,
        error,
        accessDenied,
        accessDeniedReason,
        getInfo,
        fetchProjects,
      }}
    >
      {children}
    </ClientDataContext.Provider>
  );
}

export function useClientData() {
  const context = useContext(ClientDataContext);
  if (!context) {
    throw new Error("useClientData must be used within a ClientDataProvider");
  }
  return context;
}
